import { useCallback, useEffect, useState } from 'react';

import { AnalyticsContext } from '../../entities/analytics-context';
import { ChatMessageAction } from '../../entities/chat-message-action';
import { auth } from '../firebase/firebase';
import useSocket from './socket-hook';

export interface DashboardChatMessage {
  sender: string;
  text: string;
  isUser: boolean;
  hidden?: boolean;
  createdAt: number;
  typing?: boolean; // true while llmNewToken stream is in progress
  actions?: ChatMessageAction[]; // present only on agent replies that offer a CTA, e.g. "Connect Jira"
}

type AgentStepType =
  | 'coordinator_classify'
  | 'coordinator_route'
  | 'tool_call'
  | 'tool_result'
  | 'llm_token'
  | 'final_answer';

interface AgentStep {
  type: AgentStepType;
  name?: string;
  agentName?: string; // e.g. 'JiraAgentV2' | 'TrelloAgent' | 'StandaloneAgent' — same values chatMessage.sender now carries
}

interface SendOptions {
  projectId?: string;
  taskManager?: string;
  sessionId?: string;
  analyticsContext?: AnalyticsContext;
  connectedAgents?: string[];
  docId?: string;
}

export function useDashboardChat() {
  const userId = auth.currentUser?.uid ?? 'anonymous';
  const { emit, on, off, uploadFile } = useSocket(userId);

  const [liveMessages, setLiveMessages] = useState<DashboardChatMessage[]>([]);
  const [typingText, setTypingText] = useState('');

  useEffect(() => {
    // Best-effort label for a still-streaming placeholder, before the final chatMessage confirms
    // the real responding agent — updated from agentStep's agentName as steps arrive (usually
    // resolved to the right agent well before the LLM starts streaming its answer).
    let currentAgentName = 'MFlow Agent';

    const onMessage = (msg: DashboardChatMessage & { sender: string }) => {
      setTypingText('');
      setLiveMessages((prev) => {
        const last = prev[prev.length - 1];
        // If a streaming message is in progress from the agent, finalize it — merge the whole
        // payload (sender/actions/etc.), not just text, since the final chatMessage is the only
        // place the real agent name and any actions arrive (websocket.md, 2026-08-16: `sender` is
        // now the actual responding agent's name, not the literal 'Agent').
        if (last && !last.isUser && last.typing) {
          const updated = [...prev];
          updated[updated.length - 1] = { ...last, ...msg, typing: false };
          return updated;
        }
        return [...prev, { ...msg, isUser: msg.sender === userId }];
      });
    };

    const onTyping = ({ sender }: { sender: string }) => {
      setTypingText(`${sender} is thinking...`);
    };

    const onProcess = ({ message, processTitle }: { message?: DashboardChatMessage; processTitle?: string }) => {
      if (message) {
        setLiveMessages((prev) => [...prev, { ...message, isUser: false }]);
      }
      if (processTitle) setTypingText(processTitle);
    };

    const onToken = ({ type, token }: { type: string; token: string; sessionId?: string; runId?: string }) => {
      if (type !== 'token') return;
      setTypingText('');
      setLiveMessages((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (!last || last.isUser || !last.typing) {
          updated.push({
            sender: currentAgentName,
            text: token ?? '',
            isUser: false,
            createdAt: Date.now(),
            typing: true,
          });
          return updated;
        }
        updated[updated.length - 1] = { ...last, text: (last.text ?? '') + (token ?? '') };
        return updated;
      });
    };

    const toolLabels: Record<string, string> = {
      rag_search: 'Searching Jira data…',
      jira_create_issue: 'Creating issue…',
      jira_update_issue: 'Updating issue…',
    };

    const onAgentStep = (step: AgentStep) => {
      if (step.agentName) currentAgentName = step.agentName;
      if (step.type === 'final_answer') {
        setTypingText('');
        return;
      }
      if (step.type === 'coordinator_classify') {
        setTypingText('Understanding your request…');
        return;
      }
      if (step.type === 'coordinator_route') {
        setTypingText('Routing…');
        return;
      }
      if (step.type === 'tool_call') {
        setTypingText(step.name ? (toolLabels[step.name] ?? `Calling ${step.name}…`) : 'Working…');
        return;
      }
      if (step.type === 'tool_result') {
        setTypingText('Got context, thinking…');
        return;
      }
    };

    on('chatMessage', onMessage);
    on('typing', onTyping);
    on('process:message', onProcess);
    on('llmNewToken', onToken);
    on('agentStep', onAgentStep);

    return () => {
      off('chatMessage', onMessage as (...args: unknown[]) => void);
      off('typing', onTyping as (...args: unknown[]) => void);
      off('process:message', onProcess as (...args: unknown[]) => void);
      off('llmNewToken', onToken as (...args: unknown[]) => void);
      off('agentStep', onAgentStep as (...args: unknown[]) => void);
    };
  }, [userId, off, on]);

  const clearMessages = useCallback(() => {
    setLiveMessages([]);
    setTypingText('');
  }, []);

  const sendMessage = (text: string, opts: SendOptions = {}) => {
    if (!text.trim()) return;

    const msg: DashboardChatMessage = {
      sender: userId,
      text,
      isUser: true,
      createdAt: Date.now(),
    };

    emit('chatMessage', {
      sender: userId,
      text,
      sessionId: opts.sessionId,
      analyticsContext: opts.analyticsContext,
      connectedAgents: opts.connectedAgents,
      context: {
        taskManager: opts.taskManager === 'general' ? undefined : opts.taskManager,
        boardId: opts.projectId,
        // Plural `docIds` is the current contract (MVPB-54) — the singular `docId` field still
        // works server-side for back-compat but is deprecated, so send the preferred shape even
        // though this hook's own API is still single-file.
        docIds: opts.docId ? [opts.docId] : undefined,
      },
      createdAt: Date.now(),
    });

    setLiveMessages((prev) => [...prev, msg]);
  };

  return { liveMessages, typingText, sendMessage, clearMessages, uploadFile };
}
